import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { CloseSmallIcon, InfoFilledIcon, RadioOnIcon, WarningFilledIcon } from "src/foundations/icons";
import Paper, { PaperProps } from "@mui/material/Paper";
import { ReactElement, SyntheticEvent } from "react";
import {
    BottomComponentBox,
    FlexBox,
    StyledNotificationBanner,
    TitleBox,
} from "src/components/feedback/senec-alert/SenecNotificationBanner.styled";

export interface SenecNotificationBannerProps {
    /** banner is not displayed if false */
    open?: boolean;
    /** Handler that is called on close btn click */
    onClose?: (event: Event | SyntheticEvent<Element, Event>) => void;
    /** Determines the color and icon of the banner, indicating the type of banner. */
    severity?: "error" | "warning" | "info" | "success";
    /** The primary text displayed at the top of the banner, indicating the main message. */
    title: string;
    /** Optional text or React element displayed below the title, providing additional details. */
    subtitle?: string | ReactElement;
    /** Optional React element displayed at the bottom of the banner, which can be used for actions or additional information. */
    BottomComponent?: ReactElement;
    /** Optional attribute used for testing, defaults to "senec-notification-banner" if not specified.*/
    "data-testid"?: string;
    /** Optional styling props for the mui paper component the notification banner is based on */
    sx?: PaperProps["sx"];
}

/**
 * The notification banner displays a main title, an (optional) subtitle, an (optional) close btn, and a (optional) custom component at the bottom.
 * The component supports different severity levels which affect the visual styling.
 */
export const SenecNotificationBanner = ({
    open = true,
    title,
    subtitle,
    onClose,
    severity = "info",
    "data-testid": dataTestId = "senec-notification-banner",
    BottomComponent,
    sx,
}: SenecNotificationBannerProps) => {
    if (!open) return <></>;

    return (
        <StyledNotificationBanner data-testid={dataTestId} severity={severity} sx={sx}>
            <Paper elevation={0}>
                <NotificationHeader title={title} severity={severity} onClose={onClose} />
                <Stack direction={"column"} spacing={4}>
                    {subtitle && (
                        <Box sx={{ paddingTop: "16px" }}>
                            {typeof subtitle === "string" ? (
                                <Typography variant={"s1"} sx={{ wordBreak: "break-word" }}>
                                    {subtitle}
                                </Typography>
                            ) : (
                                subtitle
                            )}
                        </Box>
                    )}
                    {BottomComponent && <BottomComponentBox>{BottomComponent}</BottomComponentBox>}
                </Stack>
            </Paper>
        </StyledNotificationBanner>
    );
};

const NotificationHeader = ({ title, onClose, severity }: Omit<SenecNotificationBannerProps, "subtitle">) => {
    const theme: SenecBaseTheme = useTheme();

    const iconMapping = {
        warning: <WarningFilledIcon htmlColor={theme.palette.yellow.main} sx={iconStyles} data-testid="warning-icon" />,
        error: <WarningFilledIcon htmlColor={theme.palette.red.main} data-testid="error-icon" sx={iconStyles} />,
        info: <InfoFilledIcon data-testid="info-icon" sx={iconStyles} />,
        success: <RadioOnIcon data-testid="success-icon" sx={iconStyles} />,
    };

    const Icon = iconMapping[severity!];

    return (
        <TitleBox>
            <FlexBox>
                <FlexBox>{Icon}</FlexBox>
                <Typography variant={"h5"}>{title}</Typography>
            </FlexBox>
            <Box sx={{ display: "flex", gap: "1rem", alignSelf: "center" }}>
                {onClose && (
                    <IconButton
                        size="small"
                        aria-label="close"
                        onClick={onClose}
                        sx={{ marginLeft: "0 !important", alignSelf: "start" }}
                    >
                        <CloseSmallIcon htmlColor={theme.palette.neutralGray100.main} />
                    </IconButton>
                )}
            </Box>
        </TitleBox>
    );
};

const iconStyles = { alignSelf: "start", marginRight: 1.5 };
